import type { UnifiedAuthStep } from '~kui/types/auth';
import type { Ref } from 'vue';
import type { LocationQuery } from 'vue-router';

import type { AuthAnalyticsMethodValue } from '~/constants/auth';
import type { SelectedEntityInput, SiteRoleType } from '~/graphql/generated/graphql';
import type { CompletedFlowResult, ResumableLoginStep, SignInContinuation } from '~/types/login';
import type { ProviderLoginChoice } from '~/utils/providerLogin';

import { AuthAnalyticsMethod } from '~/constants/auth';
import { LoginFlowType } from '~/graphql/generated/graphql';
import {
  isResumableLoginStep,
  LOGIN_RESUME_QUERY_KEY,
  STEP_CHOOSE_ACCOUNT,
  STEP_ENTER_PASSWORD,
} from '~/types/login';
import { isSchoolAccountEmailError } from '~/utils/unified/schoolAccountEmail';

import type { UnifiedAuthState } from './useUnifiedAuthFlow';

import { useLoginResumeUrl } from './useLoginResumeUrl';

const RESUMED_STEPS: Record<ResumableLoginStep, UnifiedAuthStep> = {
  [STEP_ENTER_PASSWORD]: 'password',
  [STEP_CHOOSE_ACCOUNT]: 'choose-account',
};

const PARKED_STEPS: Partial<Record<UnifiedAuthStep, ResumableLoginStep>> = {
  'password': STEP_ENTER_PASSWORD,
  'choose-account': STEP_CHOOSE_ACCOUNT,
};

/** The step another page, or a reload, opens the card on for a sign-in the server parked. */
export const resumedUnifiedAuthStep = ({ query, prefilledEmail }: { query: LocationQuery; prefilledEmail: string }): UnifiedAuthStep | null => {
  const resume = query[LOGIN_RESUME_QUERY_KEY];
  return prefilledEmail && isResumableLoginStep(resume) ? RESUMED_STEPS[resume] : null;
};

interface SignInContinuationOptions {
  roleType: SiteRoleType;
  authState: Ref<UnifiedAuthState>;
  currentStep: Ref<UnifiedAuthStep>;
  loading: Ref<boolean>;
  direction: Ref<'forward' | 'backward'>;
  globalError: Ref<string | null>;
  goToStep: (step: UnifiedAuthStep) => void;
  /** The email the link arrived with, which {@link reset} puts back. */
  prefilledEmail: Ref<string>;
  reset: () => void;
  /** The step the card opened on for a sign-in another page, or a reload, handed over. */
  resumedStep: UnifiedAuthStep | null;
  /** The arrival entity the login carries to the server; only a door that signs students up has one. */
  selectedEntity: SelectedEntityInput | null;
  /** The provider of a social sign-in the callback handed over. */
  socialProvider: string;
  /** The integration provider sign-in the provider login page handed over, if it was one. */
  providerLoginChoice: ProviderLoginChoice | null;
  goToPasswordResetStep: (token: string, onDone: () => Promise<CompletedFlowResult>) => void;
  completeExistingRoleLogin: (method: AuthAnalyticsMethodValue) => Promise<CompletedFlowResult>;
  completeMatchedInviteSignup: (method: AuthAnalyticsMethodValue, accountCreated: boolean) => Promise<CompletedFlowResult>;
  startNewRoleRegistration: (method: AuthAnalyticsMethodValue, flow: LoginFlowType) => Promise<CompletedFlowResult>;
  /** Sends a sign-up refused for a school account's email to the page that explains it. */
  leaveForSchoolAccount: (method: AuthAnalyticsMethodValue) => Promise<void>;
}

/**
 * The code, and where a credential the server accepted leads. On the schools site a code can still
 * owe the password, and either can owe a choice of school account; neither has signed anyone in
 * yet, and the server holds that sign-in until it is finished. The URL names the step it waits at,
 * so a reload opens it again. Everything else signs in or signs up, the way it always has.
 */
export const useSignInContinuation = ({
  roleType,
  authState,
  currentStep,
  loading,
  direction,
  globalError,
  goToStep,
  prefilledEmail,
  reset,
  resumedStep,
  selectedEntity,
  socialProvider,
  providerLoginChoice,
  goToPasswordResetStep,
  completeExistingRoleLogin,
  completeMatchedInviteSignup,
  startNewRoleRegistration,
  leaveForSchoolAccount,
}: SignInContinuationOptions) => {
  const { $analytics } = useNuxtApp();
  const { t } = useI18n();
  const { verifyLoginCode } = useAuth();
  const { signInAwaitsPassword } = useSchoolsSignIn();
  const { parkOnUrl } = useLoginResumeUrl({ email: () => authState.value.email, currentStep, isParked: step => !!PARKED_STEPS[step] });

  /** How the credential that led to a choice of account was proved, for the sign-in the choice finishes. */
  const resumedChoiceMethod = (): AuthAnalyticsMethodValue => {
    if (resumedStep !== 'choose-account') {
      return AuthAnalyticsMethod.EmailOtp;
    }
    if (providerLoginChoice) {
      return providerLoginChoice.provider;
    }
    return socialProvider ? `social_${socialProvider.toLowerCase()}` : AuthAnalyticsMethod.EmailOtp;
  };
  let accountChoiceMethod = resumedChoiceMethod();

  const parkAt = (step: UnifiedAuthStep) => {
    goToStep(step);
    const resume = PARKED_STEPS[step];
    if (resume) {
      parkOnUrl(resume);
    }
  };

  const continueSignIn = async (
    { flow, hasMatchedInvite, passwordChangeToken = null, accountCreated = false }: SignInContinuation,
    method: AuthAnalyticsMethodValue,
  ): Promise<CompletedFlowResult> => {
    if (flow === LoginFlowType.PasswordRequired) {
      parkAt('password');
      // The password sits before the code in STEP_ORDER for the consumer site's password-first
      // lane; here it comes after the code, so the step still slides on.
      direction.value = 'forward';
      loading.value = false;
      return { flow, error: null };
    }

    if (flow === LoginFlowType.AccountChoiceRequired) {
      accountChoiceMethod = method;
      parkAt('choose-account');
      loading.value = false;
      return { flow, error: null };
    }

    authState.value.flow = flow;

    // An admin still owes the second factor and holds no session until it is given, so the MFA
    // flows stop on their step rather than completing anything.
    if (flow === LoginFlowType.MfaEnrolmentRequired || flow === LoginFlowType.MfaChallengeRequired) {
      authState.value.method = method;
      goToStep(flow === LoginFlowType.MfaEnrolmentRequired ? 'mfa-enrol' : 'mfa-challenge');
      loading.value = false;
      return { flow, error: null };
    }

    if (flow === LoginFlowType.ExistingRole) {
      if (passwordChangeToken) {
        // Leaving the step finishes the sign-in; a refusal there has no caller left to show it, so it is said here.
        goToPasswordResetStep(passwordChangeToken, async () => {
          const result = await completeExistingRoleLogin(method);
          if (result.error === 'access-denied') {
            globalError.value = t('general.auth.accessDenied');
          }
          return result;
        });
        loading.value = false;
        return { flow, error: null };
      }
      return await completeExistingRoleLogin(method);
    }

    if (hasMatchedInvite) {
      return await completeMatchedInviteSignup(method, accountCreated);
    }

    return await startNewRoleRegistration(method, flow);
  };

  const submitOtp = async (code: string) => {
    loading.value = true;

    const { flow, hasMatchedInvite, accountCreated, error } = await verifyLoginCode({
      roleType,
      code: convertToString(code),
      email: authState.value.email,
      ...(selectedEntity ? { selectedEntity } : {}),
    });

    if (isSchoolAccountEmailError(error)) {
      await leaveForSchoolAccount(AuthAnalyticsMethod.EmailOtp);
      return { flow: null, error: null };
    }

    if (!flow) {
      loading.value = false;
      return { flow: null, error };
    }

    if (flow === LoginFlowType.InvalidCode) {
      $analytics.track(EVENTS.LOGIN_OTP_SUBMITTED, { correct: 'no' });
      loading.value = false;
      return { flow: LoginFlowType.InvalidCode, error: null };
    }

    $analytics.track(EVENTS.LOGIN_OTP_SUBMITTED, { correct: 'yes' });

    return await continueSignIn({ flow, hasMatchedInvite, accountCreated }, AuthAnalyticsMethod.EmailOtp);
  };

  const continueAfterAccountChoice = (result: SignInContinuation) => {
    loading.value = true;
    return continueSignIn(result, accountChoiceMethod);
  };

  /**
   * Back to the email step with the reason, for a sign-in the server no longer holds. The email is
   * cleared, even one that arrived in the link, and stays cleared on every later way back: a
   * prefilled email step has never been touched, so its submit stays disabled.
   */
  const restartSignIn = (message: string) => {
    prefilledEmail.value = '';
    reset();
    globalError.value = message;
  };

  /** A password step the URL reopened stands only while the server still holds the sign-in for it. */
  if (resumedStep === 'password') {
    onMounted(async () => {
      const { awaitsPassword, error } = await signInAwaitsPassword(authState.value.email);
      if (!error && !awaitsPassword && currentStep.value === 'password') {
        restartSignIn(t('schools.auth.signInExpired'));
      }
    });
  }

  return { submitOtp, continueSignIn, continueAfterAccountChoice, restartSignIn };
};
